<template>
  <div v-if="rows.length > 0" class="preview-table">
    <table class="preview-table__table">
      <thead>
        <tr>
          <th class="preview-table__select">
            <ReviewSelectionHeaderCell
              :all-selected="allSelected"
              :indeterminate="indeterminate"
              :disabled="noSelectableRows"
              :label="selectionLabels.selectAll"
              :test-id="TestId.rsvpInvite.selectAll"
              @toggle-all="emit('toggle-all')"
            />
          </th>
          <th class="preview-table__status"></th>
          <th v-for="header in headers" :key="header">{{ header }}</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(row, index) in rows" :key="index" :class="rowClass(row)">
          <td class="preview-table__select">
            <!-- Existing/error rows are non-actionable: no checkbox, just an empty cell. -->
            <ReviewSelectionCell
              v-if="row.valid"
              :checked="isRowSelected(index)"
              :label="includeRowLabel(row, index)"
              :test-id="TestId.rsvpInvite.rowCheckbox"
              @toggle="emit('toggle-row', index)"
            />
          </td>
          <td class="preview-table__status">
            <span v-if="row.valid" class="preview-table__status--ok">&#10003;</span>
            <sp-tooltip v-else-if="row.existing" position="right" theme="dark" content="Already registered">
              <span class="preview-table__status--existing">&#9888;</span>
            </sp-tooltip>
            <sp-tooltip v-else position="right" theme="dark" :content="rowErrorText(row)">
              <span class="preview-table__status--error">&#10007;</span>
            </sp-tooltip>
          </td>
          <td v-for="header in headers" :key="header">{{ row.values[header] }}</td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { TestId } from "#/test-ids";
import ReviewSelectionCell from "#ce/apps/SpAdmin/shared/components/ReviewSelectionCell.ce.vue";
import ReviewSelectionHeaderCell from "#ce/apps/SpAdmin/shared/components/ReviewSelectionHeaderCell.ce.vue";

const props = defineProps({
  headers: {
    type: Array,
    default: () => [],
  },
  rows: {
    type: Array,
    default: () => [],
  },
  // Selection state comes from the store's row selection (keyed by row index);
  // this component stays presentational and emits toggles back up.
  selectedKeys: {
    type: Array,
    default: () => [],
  },
  allSelected: {
    type: Boolean,
    default: false,
  },
  indeterminate: {
    type: Boolean,
    default: false,
  },
  // The store's `labels.selection` group: selectAll / includeRow templates.
  selectionLabels: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(["toggle-row", "toggle-all"]);

const noSelectableRows = computed(() => !props.rows.some((row) => row.valid));

// Set lookup keeps a render O(rows).
const selectedKeySet = computed(() => new Set(props.selectedKeys));

function isRowSelected(index) {
  return selectedKeySet.value.has(index);
}

// Rows carry no stable name, so the accessible label names the row by its
// first-column value (typically the email address), falling back to the
// 1-based row number.
function includeRowLabel(row, index) {
  const name = row.values?.[props.headers[0]] || String(index + 1);

  return String(props.selectionLabels.includeRow ?? "").replaceAll("%{name}", name);
}

function rowClass(row) {
  if (row.existing) {
    return "preview-table__row--existing";
  }

  if (!row.valid) {
    return "preview-table__row--error";
  }

  return "";
}

function rowErrorText(row) {
  return row.errors?.join(", ") || "Invalid entry";
}
</script>

<style scoped lang="scss">
.preview-table {
  flex: 1 1 0;
  min-height: 0;
  overflow-y: auto;
  border: var(--sp-sys-border-width-default) solid var(--sp-sys-color-outline-variant, #f9f9f9);
  border-radius: var(--sp-ref-scale-4);

  &__table {
    width: 100%;
    border-collapse: collapse;
    font-size: var(--sp-sys-typescale-small-font-size, 0.875rem);

    th,
    td {
      padding: var(--sp-ref-scale-8);
      text-align: left;
      border-bottom: var(--sp-sys-border-width-default) solid var(--sp-sys-color-outline-variant, #f9f9f9);
    }

    th {
      font-size: var(--sp-ref-font-size-small, 0.625rem);
      font-weight: var(--sp-ref-font-weight-normal, 400);
      letter-spacing: 0.5px;
      color: var(--sp-sys-color-on-surface-variant, #4a4a4a);
      position: sticky;
      top: 0;
    }

    tbody tr:last-child td {
      border-bottom: none;
    }

    tbody tr:nth-child(even) {
      background-color: var(--sp-sys-color-surface-variant, #f9f9f9);
    }
  }

  &__select {
    width: var(--sp-ref-scale-32);
    text-align: center;
  }

  &__status {
    width: 2rem;
    text-align: center;

    &--ok {
      color: var(--sp-sys-color-success, #2e7d32);
    }

    &--existing {
      color: var(--sp-sys-color-warning, #f57c00);
    }

    &--error {
      color: var(--sp-sys-color-error, #d32f2f);
    }
  }
}
</style>
